// Mirrors backend cpmSubmissions/clipDuration: a campaign may require clips of
// at least `min_clip_duration_seconds`, compared in whole seconds (TikTok and
// YouTube report integers, Instagram and Facebook decimals).

/** Same bound the backend validates (`min_clip_duration_seconds` 1–3600). */
export const MAX_MIN_CLIP_DURATION_SECONDS = 3600;

const finite = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
);

export function minClipDurationSeconds(campaign: { min_clip_duration_seconds?: number | null } | null | undefined): number | null {
  const value = campaign?.min_clip_duration_seconds;
  return Number.isInteger(value) && (value as number) > 0 ? value as number : null;
}

/** "7s", "1min", "1min 05s"; null for an unknown length. */
export function formatClipDuration(seconds: number | null | undefined): string | null {
  const known = finite(seconds);
  if (known === null) return null;
  const whole = Math.round(known);
  if (whole < 60) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest ? `${minutes}min ${String(rest).padStart(2, '0')}s` : `${minutes}min`;
}

export type ClipDurationReviewStatus = 'ok' | 'below' | 'unmeasured';

/**
 * What a reviewer should know about a clip's length. `unmeasured`: the
 * campaign has a minimum but the platform did not give the length (or the clip
 * predates the rule), so it needs a manual check. `below` only shows for clips
 * admitted before the minimum was set or raised.
 */
export function clipDurationReviewStatus({ minSeconds, durationSeconds }: {
  minSeconds: number | null; durationSeconds: number | null | undefined;
}): ClipDurationReviewStatus {
  if (minSeconds === null) return 'ok';
  const known = finite(durationSeconds);
  if (known === null) return 'unmeasured';
  return Math.round(known) < minSeconds ? 'below' : 'ok';
}

/** Admin form input: empty or non-positive clears the rule; capped at the backend bound. */
export function parseMinClipDurationInput(value: string): number | null {
  if (!value.trim()) return null;
  const seconds = Math.round(Number(value));
  if (!Number.isFinite(seconds) || seconds < 1) return null;
  return Math.min(seconds, MAX_MIN_CLIP_DURATION_SECONDS);
}
